import type { IsoDate, Measurement } from "./types.js";
import type { Direction, MeasurementDirections } from "../../shared/trend-direction.js";
import type { SeasonBaseline } from "./plan.js";
import { RACE_DATE_LABEL, TARGET_END_WEIGHT, targetWeight } from "./plan.js";
import { formatDate, daysBetween } from "./dates.js";
import { fmt } from "./numbers.js";
import type { WeightTrend } from "./measurements.js";
import { rollingAverage, weightTrend } from "./measurements.js";

/**
 * 量測資料的兩種形狀，收成一個 discriminated union。
 *
 * 監督者在揭曉日之前拿到的是**無單位的 0–1**，本人與揭曉之後拿到的是公斤
 * （見 `docs/adr/0004`）。先前這件事由 `AppData` 上並排的三個欄位表示
 * （`measurements` / `redacted` / `directions`），於是「遮蔽時不可以拿這些
 * 數字算任何有單位的東西」這條規則被 13 個呼叫端各自重新判斷一次，每一處
 * 只由一段註解守著。
 *
 * 而違反它**不會報錯**：正規化後的 0–1 長得跟公斤一模一樣，`bmrEstimate`
 * 會照算出 `10 × 0.5 + …`，`weightBadges()` 會算出一整牆假的「已達成」，
 * 畫面上每一格都還在，只是全錯。ADR 0004 早就指出正確的防護是「讓型別逼
 * 呼叫端分辨」——這個檔案就是那句話。
 *
 * **`ShapelessRow` 的欄位刻意不叫 `weight_kg`。** 不改名的話這個 union 只是
 * 裝飾：兩支的 row 結構相同，`view.rows[0].weight_kg` 照樣寫得出來，型別一個
 * 錯都攔不到。改名之後那一行在遮蔽分支上直接編譯失敗。
 *
 * 與 `src/domain/` 的其他模組一樣：純函式、不讀系統時間、不讀全域狀態。
 */

/**
 * 遮蔽時每一天的形狀。
 *
 * 數值是**無單位的 0–1**：不能相減、不能比門檻、不能拿去算 BMR，也不能還原
 * 成公斤。它唯一的用途是畫出起伏的形狀。
 *
 * `bmi` 不在裡面——它與體重差一個固定的身高平方，起始體重已知就解得出身高，
 * 之後每天的 BMI 都能還原成公斤。它沒有非數字的呈現方式，所以整個不給。
 */
export interface ShapelessRow {
  measurement_date: IsoDate;
  weight: number | null;
  fat: number | null;
  lean: number | null;
}

export type MeasurementView =
  | {
    kind: "revealed";
    rows: Measurement[];
    /** 建構時算一次，月曆才不會每格掃一遍整份資料。 */
    byDate: Record<IsoDate, Measurement>;
  }
  | {
    kind: "shapeOnly";
    rows: ShapelessRow[];
    byDate: Record<IsoDate, ShapelessRow>;
    /** 伺服器算好的升降方向。死區的單位是公斤，前端算不了（ADR 0004）。 */
    directions: MeasurementDirections;
  };

/** 遮蔽了但方向沒送過來時用的空值。所有格子都落到「資料不足」。 */
const NO_DIRECTIONS: MeasurementDirections = {
  trend: { weight: null, weeklyChange: null, leanChange: null, avgFat7: null, prediction: null },
  daily: {}
};

function indexBy<T extends { measurement_date: IsoDate }>(rows: T[]): Record<IsoDate, T> {
  return Object.fromEntries(rows.map(row => [row.measurement_date, row]));
}

/**
 * 由 `/api/measurements` 的回應建一次。整個 app 只有 `src/main.js` 呼叫它。
 *
 * **`redacted` 為真時一律落到 `shapeOnly`，即使 `directions` 沒送過來。**
 * 安全的方向是不給公斤——與 `shared/roles.ts` 那兩支遮蔽函式「是本人才給」
 * 的寫法同一條理由：欄位漏傳、拼錯或伺服器改壞時，往嚴的方向壞比較便宜。
 * 掉回 `revealed` 的話畫面會直接把 0–1 當成公斤印出來。
 */
export function measurementView(
  rows: Measurement[],
  redacted: boolean,
  directions: MeasurementDirections | null
): MeasurementView {
  if (!redacted) return { kind: "revealed", rows, byDate: indexBy(rows) };

  const shapeless: ShapelessRow[] = rows.map(row => ({
    measurement_date: row.measurement_date,
    weight: row.weight_kg,
    fat: row.body_fat_percent,
    lean: row.lean_body_mass_kg
  }));

  return {
    kind: "shapeOnly",
    rows: shapeless,
    byDate: indexBy(shapeless),
    directions: directions || NO_DIRECTIONS
  };
}

/**
 * 公斤數，遮蔽時是空陣列。
 *
 * 給**只有本人走得到**的那幾條路用（戰報、文字摘要、CSV 匯出、代謝設定）：
 * 那些畫面對監督者根本不存在，但它們吃的是 `Measurement[]`，總得有一個取值
 * 的方式。回空陣列而不是丟例外——所有下游函式在空資料下都回 `null`／「—」，
 * 而丟例外會讓一個純粹不該發生的情況變成當掉。
 *
 * **不要拿它當繞過 union 的捷徑。** 會出現在監督者畫面上的東西一律用
 * `trendCells` / `chartSeries` / `dayMeasurement`。
 */
export function revealedRows(view: MeasurementView): Measurement[] {
  return view.kind === "revealed" ? view.rows : [];
}

/**
 * 月曆格子與日編輯器要的那一天。
 *
 * **形狀鏡射 `MeasurementView` 的兩支**，不是一個帶著三個可選欄位的物件。
 * 「這天有量」與「這天有數字」在遮蔽分支上是兩件事——那天有量、格子裡要畫
 * 箭頭，但公斤不存在。用同一個物件裝兩種情形的話，那層關聯只活在
 * 註解裡，呼叫端仍然要自己記得哪個欄位在哪一支才有值。
 */
export type DayMeasurement =
  | { kind: "revealed"; measurement: Measurement | null }
  | {
    kind: "shapeOnly";
    /**
     * 那天有沒有量。
     *
     * 「有沒有」不是數值，所以遮蔽時照樣答得出來——`playerStats` 的 EXP
     * 只用它加分，監督者看到的等級與連勝因此是對的。
     */
    measured: boolean;
    /** 升降方向。`null` 代表這天沒有方向（第一筆，或前一筆沒量）。 */
    direction: Direction | null;
  };

export function dayMeasurement(view: MeasurementView, date: IsoDate): DayMeasurement {
  if (view.kind === "revealed") {
    return { kind: "revealed", measurement: view.byDate[date] || null };
  }
  return {
    kind: "shapeOnly",
    measured: view.byDate[date] !== undefined,
    direction: view.directions.daily[date] ?? null
  };
}

/** 線圖要的三件事一起決定：資料、series、要不要無刻度。 */
export interface ChartSpec {
  rows: Record<string, unknown>[];
  series: { key: string; label: string; dashed?: boolean }[];
  unit: string;
  scaleless: boolean;
}

export type ChartMetric = "weight" | "fat" | "lean";

/** 每一種線圖的：兩支各自的欄位名、平均視窗、單位、預設標籤。 */
const CHART_CONFIG: Record<ChartMetric, {
  revealedKey: keyof Measurement & string;
  shapelessKey: keyof ShapelessRow & string;
  window: number;
  unit: string;
  labels: [string, string];
}> = {
  weight: {
    revealedKey: "weight_kg", shapelessKey: "weight", window: 7, unit: "kg",
    labels: ["每日體重", "7日平均"]
  },
  fat: {
    revealedKey: "body_fat_percent", shapelessKey: "fat", window: 7, unit: "%",
    labels: ["每日體脂", "7日平均"]
  },
  lean: {
    revealedKey: "lean_body_mass_kg", shapelessKey: "lean", window: 28, unit: "kg",
    labels: ["每日淨體重", "28日平均"]
  }
};

/** 目標線的預設標籤。統計頁叫「目標線」，分享頁叫「目標」——那是排版。 */
const TARGET_LABEL = "目標線";

/**
 * 一張線圖需要的全部東西。
 *
 * **「遮蔽時不畫目標線」這條規則收在這裡。** `targetWeight()` 是公開的線性
 * 公式，畫在跟體重線同一條 y 軸上——有它當尺，兩個點就能校準整條軸，拿不拿
 * 掉刻度標籤都一樣。那是 ADR 0004 點名的四條洩漏管道之一，不能留給呼叫端
 * 記得。
 *
 * `labels` 讓呼叫端覆寫每一條線的名字（分享頁的圖例比統計頁短）。標籤是排版，
 * 各自決定；資料、欄位名與 `scaleless` 不是。
 *
 * `baseline` 是目標線的起點。沒有的話就不畫目標線，不編一條假的。
 */
export function chartSeries(
  view: MeasurementView,
  metric: ChartMetric,
  labels?: string[],
  baseline: SeasonBaseline | null = null
): ChartSpec {
  const config = CHART_CONFIG[metric];
  const label = (index: number, fallback: string) => labels?.[index] ?? fallback;

  if (view.kind === "shapeOnly") {
    const key = config.shapelessKey;
    return {
      rows: rollingAverage(
        view.rows.filter(row => Number.isFinite(row[key] as number)), key, config.window
      ),
      series: [
        { key, label: label(0, config.labels[0]) },
        { key: `${key}_avg${config.window}`, label: label(1, config.labels[1]) }
      ],
      unit: config.unit,
      scaleless: true
    };
  }

  const key = config.revealedKey;
  const rows = rollingAverage(
    view.rows.filter(row => Number.isFinite(row[key] as number)), key, config.window
  );

  return {
    rows: metric === "weight" && baseline
      ? rows.map(row => ({ ...row, target: targetWeight(row.measurement_date, baseline) }))
      : rows,
    series: [
      { key, label: label(0, config.labels[0]) },
      { key: `${key}_avg${config.window}`, label: label(1, config.labels[1]) },
      ...(metric === "weight" && baseline
        ? [{ key: "target", label: label(2, TARGET_LABEL), dashed: true }]
        : [])
    ],
    unit: config.unit,
    scaleless: false
  };
}

/**
 * 趨勢摘要的格式化原子。
 *
 * CONTEXT.md 說趨勢摘要「是一個整體，不是七個各自算的數字——首頁、統計、
 * 分享頁與戰報看到的必須是同一組」。鐵則 5 把**數字**收成單一來源
 * （`weightTrend()` / `battleReport()`），但**寫法**先前有四份：三個分頁各自
 * 內聯，`summary.ts` 另有自己的 `kg()` 與 `signed()`。而文字戰報是四份裡唯一
 * 沒有基準截圖在守的呈現，也就是最容易悄悄漂開的那一份。
 *
 * 資料不足一律是「—」，不是 0——那兩件事在畫面上必須分得出來。
 */
export function kgText(value: number | null | undefined): string {
  return Number.isFinite(value as number) ? `${fmt(value as number)} kg` : "—";
}

/** 帶正負號的公斤差。負號由 `fmt` 自己帶，所以只補正號。 */
export function signedKgText(value: number | null | undefined): string {
  if (!Number.isFinite(value as number)) return "—";
  const number = value as number;
  return `${number > 0 ? "+" : ""}${fmt(number)} kg`;
}

export function percentText(value: number | null | undefined): string {
  return Number.isFinite(value as number) ? `${fmt(value as number)}%` : "—";
}

/** 趨勢摘要的七格。順序由呼叫端決定，這裡只保證七格都在。 */
export type TrendCellKey =
  | "weight" | "gapToTarget" | "avg7" | "weeklyChange"
  | "avgFat7" | "leanChange" | "prediction";

export interface TrendCell {
  key: TrendCellKey;
  /** 預設標籤。呼叫端可以換掉——那是排版。 */
  label: string;
  /** 已格式化好的字串（含單位與正負號）。**遮蔽時是 `null`。** */
  text: string | null;
  /** 升降方向。**沒遮蔽時是 `null`。** 與 `text` 恰好一個有值。 */
  direction: Direction | null;
  /**
   * 資料不足。
   *
   * 兩支都答得出來，而且**與「持平」是不同的事**——混掉的話畫面會把「還沒量」
   * 顯示成「沒有變化」（見 `shared/trend-direction.ts` 的 `directionOf`）。
   */
  missing: boolean;
  note: string;
}

/**
 * 七格都在，所以用 Record 而不是陣列——呼叫端按 key 取，不必 `find()` 再補
 * 一個非空斷言，也不可能少列一格。
 */
export type TrendCells = Record<TrendCellKey, TrendCell>;

function cell(
  key: TrendCellKey, label: string, note: string,
  { text = null, direction = null, missing }: {
    text?: string | null; direction?: Direction | null; missing: boolean;
  }
): TrendCell {
  return { key, label, text, direction, missing, note };
}

/**
 * 最新體重那一格的副標：量測日 ＋ 與前一筆的差。
 *
 * 「較昨日」只有在前一筆真的是前一天時才寫得出來——中間漏量一天的話那三個字
 * 是錯的，所以改寫成實際日期。比較對象一律是**前一筆有體重的測量**，不是
 * 「今天減一天」（同 `shared/trend-direction.ts` 的 `dailyDirections`）。
 */
function weightNote(trend: WeightTrend): string {
  if (!trend.latestDate) return "等待同步";
  const date = formatDate(trend.latestDate);
  if (trend.previousDate === null || trend.dayChange === null) return date;
  const label = daysBetween(trend.previousDate, trend.latestDate) === 1
    ? "較昨日"
    : `較 ${formatDate(trend.previousDate)}`;
  return `${date}｜${label} ${signedKgText(trend.dayChange)}`;
}

/**
 * 趨勢摘要那一整組。
 *
 * 遮蔽時每一格只有方向，沒有公斤數；`gapToTarget` 連方向都沒有——它要跟
 * 74.9kg 比，而那是個公斤錨點，前端一旦拿得到就破功（ADR 0004 的「只能有
 * 一個參考點」）。呼叫端在遮蔽時本來就不渲染那一格。
 *
 * 與 `src/domain/` 的其他模組一樣不讀系統時間：「今天」由呼叫端傳入。
 */
export function trendCells(view: MeasurementView, today: IsoDate): TrendCells {
  if (view.kind === "shapeOnly") {
    const direction = view.directions.trend;
    const arrow = (key: TrendCellKey, label: string, note: string, value: Direction | null) =>
      cell(key, label, note, { direction: value, missing: value === null });

    return {
      weight: arrow("weight", "目前體重", `數字在 ${RACE_DATE_LABEL} 公開`, direction.weight),
      // 公斤錨點，遮蔽時連方向都不給
      gapToTarget: cell("gapToTarget", "目標差距", `距離賽事目標 ${TARGET_END_WEIGHT}kg`, { missing: true }),
      avg7: arrow("avg7", "7日平均", "較前週", direction.weeklyChange),
      weeklyChange: arrow("weeklyChange", "每週變化", "7日平均比較", direction.weeklyChange),
      avgFat7: arrow("avgFat7", "7日平均體脂", "以趨勢為主", direction.avgFat7),
      leanChange: arrow("leanChange", "28日淨體重變化", "不等於骨骼肌量", direction.leanChange),
      prediction: arrow("prediction", `${RACE_DATE_LABEL} 預估`, "依最近28日線性趨勢", direction.prediction)
    };
  }

  const trend = weightTrend(view.rows, today);
  const number = (
    key: TrendCellKey, label: string, note: string,
    value: number | null, format: (value: number | null) => string
  ) => cell(key, label, note, { text: format(value), missing: value === null });

  return {
    weight: number("weight", "目前體重", weightNote(trend), trend.weight, kgText),
    gapToTarget: number("gapToTarget", "目標差距", `距離賽事目標 ${TARGET_END_WEIGHT}kg`, trend.gapToTarget, kgText),
    avg7: number("avg7", "7日平均", "較前週", trend.avg7, kgText),
    weeklyChange: number("weeklyChange", "每週變化", "7日平均比較", trend.weeklyChange, signedKgText),
    avgFat7: number("avgFat7", "7日平均體脂", "以趨勢為主", trend.avgFat7, percentText),
    leanChange: number("leanChange", "28日淨體重變化", "不等於骨骼肌量", trend.leanChange, signedKgText),
    prediction: number(
      "prediction", `${RACE_DATE_LABEL} 預估`, "依最近28日線性趨勢",
      trend.prediction ? trend.prediction.predicted : null, kgText
    )
  };
}
